import { commandCenterHref, mountHomeLink } from '@squadlab/os-chrome';
import { adoptStyles } from '../core/adopt-styles.js';
import type { Container } from './container.js';

/**
 * Mounts the app chrome — the OS-level Command Center return link, product
 * name, primary navigation, the venture switcher (the multi-tenant seam made
 * visible) and the SIMULATION badge — above the router outlet, and returns the
 * outlet.
 *
 * Every interaction here dispatches a semantic command (ADR 0032): nav
 * buttons → `nav.home` / `people.open`; the switcher → `venture.switch`; the
 * return link → `nav.commandCenter`. DOM construction + `textContent` only
 * (ADR 0008).
 */
export function mountShell(root: HTMLElement, container: Container): HTMLElement {
  const { messages, commands, ventures, session } = container;

  const header = document.createElement('header');
  header.className = 'app-header';

  // The wordmark is the SERVICE name only (CC-35): the OS chip mounted just
  // before it already says which operating system this is, and the fully-
  // qualified `appTitle` still drives `document.title` (router.setTitle).
  const title = document.createElement('p');
  title.className = 'app-header__title';
  title.textContent = messages.appWordmark;

  const nav = document.createElement('nav');
  nav.className = 'app-header__nav';
  const homeBtn = document.createElement('button');
  homeBtn.type = 'button';
  homeBtn.className = 'app-header__link';
  homeBtn.textContent = messages.navAttention;
  homeBtn.addEventListener('click', () => commands.dispatch('nav.home', {}));
  const peopleBtn = document.createElement('button');
  peopleBtn.type = 'button';
  peopleBtn.className = 'app-header__link';
  peopleBtn.textContent = messages.navPeople;
  peopleBtn.addEventListener('click', () => commands.dispatch('people.open', {}));
  const casesBtn = document.createElement('button');
  casesBtn.type = 'button';
  casesBtn.className = 'app-header__link';
  casesBtn.textContent = messages.navCases;
  casesBtn.addEventListener('click', () => commands.dispatch('cases.open', {}));
  nav.append(homeBtn, peopleBtn, casesBtn);

  const controls = document.createElement('div');
  controls.className = 'app-header__controls';

  // Language toggle: dispatches locale.switch (ADR 0032) → clean reload.
  const otherLocale = container.config.locale.toLowerCase().startsWith('ar') ? 'en' : 'ar';
  const langBtn = document.createElement('button');
  langBtn.type = 'button';
  langBtn.className = 'app-header__lang';
  langBtn.textContent = otherLocale === 'ar' ? 'العربية' : 'English';
  langBtn.lang = otherLocale === 'ar' ? 'ar' : 'en';
  langBtn.addEventListener('click', () => commands.dispatch('locale.switch', { locale: otherLocale }));
  controls.appendChild(langBtn);

  const switcherLabel = document.createElement('label');
  switcherLabel.className = 'app-header__venture-label';
  switcherLabel.textContent = messages.ventureSwitcherLabel;
  const select = document.createElement('select');
  select.className = 'app-header__venture';
  for (const v of ventures) {
    const option = document.createElement('option');
    option.value = v.id;
    option.textContent = v.nameEn;
    select.appendChild(option);
  }
  select.value = session.getState().ventureId;
  select.addEventListener('change', () => commands.dispatch('venture.switch', { ventureId: select.value }));
  // Keep the control honest if the session changes through another input source.
  session.subscribe((s) => {
    if (select.value !== s.ventureId) select.value = s.ventureId;
  });
  switcherLabel.appendChild(select);

  const badge = document.createElement('sim-badge');
  badge.setAttribute('text', messages.badgeSimulation);
  badge.setAttribute('label', messages.badgeSimulationLabel);

  controls.append(switcherLabel, badge);
  header.append(title, nav, controls);

  const main = document.createElement('main');
  main.id = 'outlet';
  main.className = 'app-main';

  root.replaceChildren(header, main);

  // The way back to the bridge (COMMAND-4, ADR 0110): the first element of this
  // service's own top-start chrome, immediately before the wordmark. Mounted after
  // the header is in the document so the element's shadow root renders on connect.
  // The click is handed to a semantic command — the shell never navigates itself.
  // `mountShell` runs exactly once (src/main.ts) and the header lives for the
  // document's lifetime, so there is no teardown path to hook: disposal rides the
  // element's own disconnectedCallback if the header is ever replaced.
  mountHomeLink(header, {
    href: commandCenterHref(),
    label: messages.navCommandCenter,
    title: messages.navCommandCenterLabel,
    onActivate: () => commands.dispatch('nav.commandCenter', {}),
  });

  injectShellStyles();

  return main;
}

let stylesInjected = false;
function injectShellStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  adoptStyles(
    document,
    `
    /* --oschrome-* tokens inherit through the home link's shadow boundary, which is
       the only sanctioned way to theme it (ADR 0110): the OS chip wears the People
       Engine's cyan rather than the package default. */
    .app-header { display: flex; align-items: center; justify-content: flex-start; flex-wrap: wrap; gap: var(--space-2); padding-inline: var(--space-3); padding-block: var(--space-2); border-block-end: var(--hairline); --oschrome-accent: var(--color-accent); --oschrome-fg: var(--color-text); --oschrome-bg: rgba(14, 22, 38, 0.66); --oschrome-bg-strong: rgba(14, 22, 38, 0.92); }
    .app-header__title { margin: 0; font-family: var(--font-display); font-size: var(--font-size-2); letter-spacing: 0.12em; text-transform: uppercase; color: var(--color-accent); text-shadow: var(--text-holo); }
    .app-header__nav { display: flex; gap: var(--space-2); }
    .app-header__link { font: inherit; color: var(--color-text); background: none; border: 0; border-block-end: 2px solid transparent; padding-block: var(--space-1); cursor: pointer; letter-spacing: 0.06em; text-transform: uppercase; }
    .app-header__link:hover { color: var(--color-accent); border-color: var(--color-accent); }
    /* The link + wordmark lead the row (ADR 0110 §2), so the controls take the
       inline-end edge with an auto margin instead of space-between — which would
       have pulled the wordmark away from the chip that must sit beside it. */
    .app-header__controls { display: flex; align-items: center; gap: var(--space-2); margin-inline-start: auto; }
    .app-header__lang { font: inherit; color: var(--color-text); background: none; border: var(--hairline); border-radius: var(--radius); padding-block: 2px; padding-inline: var(--space-1); cursor: pointer; }
    .app-header__venture-label { display: inline-flex; align-items: center; gap: var(--space-1); color: var(--color-muted); font-size: var(--font-size-1); }
    .app-header__venture { font: inherit; color: var(--color-text); background: var(--color-surface); border: var(--hairline); border-radius: var(--radius); padding-block: 2px; padding-inline: var(--space-1); }
    .app-main { inline-size: 100%; }
    .app-main > section { padding-inline: var(--space-3); padding-block: var(--space-3); max-inline-size: 60rem; margin-inline: auto; display: block; }
    .view-status { color: var(--color-muted); padding-inline: var(--space-3); }
    h1:focus-visible, h2:focus-visible { outline: 2px solid var(--color-focus); outline-offset: 4px; }
  `,
  );
}
